import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { getAccessToken } from "../../api/getAccessToken";

export const SGP_SPORT_LIMITS_QUERY_KEY = "SGP_SPORT_LIMITS" as const;
export const SGP_SPORTS_COUNTRIES_TREE_QUERY_KEY =
  "SGP_SPORTS_COUNTRIES_TREE" as const;
export const SGP_LEAGUE_LIMITS_QUERY_KEY = "SGP_LEAGUE_LIMITS" as const;
export const SGP_LEAGUE_DEFAULT_LIMIT_SOURCE = "league_default" as const;

export type SgpSportLimitApi = {
  sport_id: string;
  sport_name?: string;
  sport?: string;
  limit: number;
};

export type SgpSportLimitRow = {
  id: string;
  sport: string;
  sportId: string;
  defaultLimit: number;
};

export type SgpSportLimitUpdateItem = {
  sport_id: string;
  limit: number;
};

export type UpdateSgpSportLimitsPayload = {
  limits: SgpSportLimitUpdateItem[];
};

export type SgpSportsCountriesTreeCountry = {
  country_id: string;
  country_name: string;
};

export type SgpSportsCountriesTreeSport = {
  sport_id: string;
  sport_name: string;
  countries: SgpSportsCountriesTreeCountry[];
};

export type SgpLeagueLimitApi = {
  league_id: string;
  league_name?: string;
  limit: number;
  limit_source?: string;
};

export type SgpLeagueLimitRow = {
  id: string;
  league: string;
  leagueId: string;
  defaultLimit: number;
  limitSource?: string;
};

export type SgpLeagueLimitUpdateItem = {
  sport_id: string;
  league_id: string;
  limit: number | null;
};

export type UpdateSgpLeagueLimitsPayload = {
  limits: SgpLeagueLimitUpdateItem[];
};

type SportLimitComparable = Pick<SgpSportLimitRow, "sportId" | "defaultLimit">;
type LeagueLimitComparable = Pick<SgpLeagueLimitRow, "leagueId" | "defaultLimit">;

type SportLimitDraftRow = {
  sportId: string;
  defaultLimit: unknown;
};

type LeagueLimitDraftRow = {
  leagueId: string;
  defaultLimit: unknown;
};

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

export function mapSgpSportLimitApiToRow(item: SgpSportLimitApi): SgpSportLimitRow {
  const sportId = String(item.sport_id ?? "");
  const sportName = String(item.sport_name ?? item.sport ?? sportId);

  return {
    id: sportId,
    sport: sportName,
    sportId,
    defaultLimit: finiteNumber(item.limit),
  };
}

function normalizeSportLimitsResponse(body: unknown): SgpSportLimitRow[] {
  const root = body as { data?: unknown } | null | undefined;
  const data = root?.data;

  if (Array.isArray(data)) {
    return data.map((item) => mapSgpSportLimitApiToRow(item as SgpSportLimitApi));
  }

  if (data && typeof data === "object" && Array.isArray((data as { limits?: unknown }).limits)) {
    return ((data as { limits: SgpSportLimitApi[] }).limits).map(mapSgpSportLimitApiToRow);
  }

  return [];
}

export function buildChangedSportLimitsPayload(
  originalRows: SportLimitComparable[],
  draftRows: SportLimitDraftRow[],
  parseLimit: (value: unknown) => number,
): SgpSportLimitUpdateItem[] {
  const originalBySportId = new Map(
    originalRows.map((row) => [row.sportId, finiteNumber(row.defaultLimit)]),
  );

  return draftRows.reduce<SgpSportLimitUpdateItem[]>((changed, row) => {
    const nextLimit = parseLimit(row.defaultLimit);
    const originalLimit = originalBySportId.get(row.sportId);

    if (originalLimit === undefined || originalLimit !== nextLimit) {
      changed.push({
        sport_id: row.sportId,
        limit: nextLimit,
      });
    }

    return changed;
  }, []);
}

export function buildChangedLeagueLimitsPayload(
  sportId: string,
  originalRows: LeagueLimitComparable[],
  draftRows: LeagueLimitDraftRow[],
  parseLimit: (value: unknown) => number,
): SgpLeagueLimitUpdateItem[] {
  const originalByLeagueId = new Map(
    originalRows.map((row) => [row.leagueId, finiteNumber(row.defaultLimit)]),
  );

  return draftRows.reduce<SgpLeagueLimitUpdateItem[]>((changed, row) => {
    const nextLimit = parseLimit(row.defaultLimit);
    const originalLimit = originalByLeagueId.get(row.leagueId);

    if (originalLimit === undefined || originalLimit !== nextLimit) {
      changed.push({
        sport_id: sportId,
        league_id: row.leagueId,
        limit: nextLimit,
      });
    }

    return changed;
  }, []);
}

function normalizeSportsCountriesTreeResponse(
  body: unknown,
): SgpSportsCountriesTreeSport[] {
  const root = body as { data?: unknown } | null | undefined;
  const data = root?.data;

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    const sport = item as SgpSportsCountriesTreeSport;
    return {
      sport_id: String(sport.sport_id ?? ""),
      sport_name: String(sport.sport_name ?? sport.sport_id ?? ""),
      countries: Array.isArray(sport.countries)
        ? sport.countries.map((country) => ({
            country_id: String(country.country_id ?? ""),
            country_name: String(country.country_name ?? country.country_id ?? ""),
          }))
        : [],
    };
  });
}

export function mergeLeagueLimitRows(
  existingRows: SgpLeagueLimitRow[],
  updatedRows: SgpLeagueLimitRow[],
): SgpLeagueLimitRow[] {
  if (updatedRows.length === 0) {
    return existingRows;
  }

  const updatedByLeagueId = new Map(
    updatedRows.map((row) => [row.leagueId, row]),
  );

  return existingRows.map((row) => updatedByLeagueId.get(row.leagueId) ?? row);
}

export function mapSgpLeagueLimitApiToRow(item: SgpLeagueLimitApi): SgpLeagueLimitRow {
  const leagueId = String(item.league_id ?? "");

  return {
    id: leagueId,
    league: String(item.league_name ?? leagueId),
    leagueId,
    defaultLimit: finiteNumber(item.limit),
    limitSource: item.limit_source ? String(item.limit_source) : undefined,
  };
}

function normalizeLeagueLimitsResponse(body: unknown): SgpLeagueLimitRow[] {
  const root = body as { data?: unknown } | null | undefined;
  const data = root?.data;

  if (Array.isArray(data)) {
    return data.map((item) => mapSgpLeagueLimitApiToRow(item as SgpLeagueLimitApi));
  }

  if (data && typeof data === "object" && Array.isArray((data as { limits?: unknown }).limits)) {
    return ((data as { limits: SgpLeagueLimitApi[] }).limits).map(mapSgpLeagueLimitApiToRow);
  }

  return [];
}

export async function fetchSgpSportLimits(): Promise<SgpSportLimitRow[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_SGP_SPORT_LIMITS);
  return normalizeSportLimitsResponse(response?.data);
}

export async function fetchSgpSportsCountriesTree(): Promise<SgpSportsCountriesTreeSport[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_SGP_SPORTS_COUNTRIES_TREE);
  return normalizeSportsCountriesTreeResponse(response?.data);
}

export async function fetchSgpLeagueLimits(
  sportId: string,
  countryId: string,
): Promise<SgpLeagueLimitRow[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_SGP_LEAGUE_LIMITS, {
    params: {
      sport_id: sportId,
      country_id: countryId,
    },
  });
  return normalizeLeagueLimitsResponse(response?.data);
}

export async function updateSgpSportLimits(
  payload: UpdateSgpSportLimitsPayload,
): Promise<SgpSportLimitRow[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(API_ROUTES.UPDATE_SGP_SPORT_LIMITS, payload);
  return normalizeSportLimitsResponse(response?.data);
}

export async function updateSgpLeagueLimits(
  payload: UpdateSgpLeagueLimitsPayload,
): Promise<SgpLeagueLimitRow[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(API_ROUTES.UPDATE_SGP_LEAGUE_LIMITS, payload);
  return normalizeLeagueLimitsResponse(response?.data);
}

export function useSgpSportLimits() {
  return useQuery<SgpSportLimitRow[], Error>({
    queryKey: [SGP_SPORT_LIMITS_QUERY_KEY],
    queryFn: fetchSgpSportLimits,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSgpSportLimits() {
  const queryClient = useQueryClient();

  return useMutation<SgpSportLimitRow[], Error, UpdateSgpSportLimitsPayload>({
    mutationFn: updateSgpSportLimits,
    onSuccess: (rows) => {
      if (rows.length > 0) {
        queryClient.setQueryData([SGP_SPORT_LIMITS_QUERY_KEY], rows);
        return;
      }
      queryClient.invalidateQueries({ queryKey: [SGP_SPORT_LIMITS_QUERY_KEY] });
    },
  });
}

export function useSgpSportsCountriesTree(enabled = true) {
  return useQuery<SgpSportsCountriesTreeSport[], Error>({
    queryKey: [SGP_SPORTS_COUNTRIES_TREE_QUERY_KEY],
    queryFn: fetchSgpSportsCountriesTree,
    enabled,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

export function useSgpLeagueLimits(
  sportId: string | null | undefined,
  countryId: string | null | undefined,
  enabled = true,
) {
  const canFetch = Boolean(enabled && sportId && countryId);

  return useQuery<SgpLeagueLimitRow[], Error>({
    queryKey: [SGP_LEAGUE_LIMITS_QUERY_KEY, sportId, countryId],
    queryFn: () => fetchSgpLeagueLimits(String(sportId), String(countryId)),
    enabled: canFetch,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSgpLeagueLimits(
  sportId: string | null | undefined,
  countryId: string | null | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation<SgpLeagueLimitRow[], Error, UpdateSgpLeagueLimitsPayload>({
    mutationFn: updateSgpLeagueLimits,
    onSuccess: (rows) => {
      const queryKey = [SGP_LEAGUE_LIMITS_QUERY_KEY, sportId, countryId];

      if (rows.length > 0) {
        const existing =
          queryClient.getQueryData<SgpLeagueLimitRow[]>(queryKey) ?? [];
        queryClient.setQueryData(queryKey, mergeLeagueLimitRows(existing, rows));
        return;
      }

      queryClient.invalidateQueries({ queryKey });
    },
  });
}
